import { useMemo, useCallback } from 'react';
import { useGetWatchlistsQuery } from '../services/edgeApi';
import { PathReport } from '@/types';
import { pathSpecKey, pathSpecsEqual } from './pathSpecUtils';

/**
 * Shared hook for managing path watchlist membership and display names.
 * Used by both AddToWatchlistButton and AddToWatchlistModal.
 */
export function usePathWatchlists(
  path: PathReport | undefined,
  options?: { enabled?: boolean },
) {
  const pathFingerprint = path?.original_path_fingerprint;
  const enabled = options?.enabled ?? true;

  const { data: watchlists = [], ...queryState } = useGetWatchlistsQuery({
    view_all: false,
    path_fingerprint: pathFingerprint,
    with_paths: true,
  }, {
    skip: !enabled || !pathFingerprint,
  });

  const getRouteMemberships = useCallback(
    (watchlist: any): any[] => {
      if (!pathFingerprint || !watchlist?.path_records_detail) return [];
      return watchlist.path_records_detail.filter(
        (record: any) => record?.path_fingerprint === pathFingerprint,
      );
    },
    [pathFingerprint],
  );

  const getExactMemberships = useCallback(
    (watchlist: any): any[] =>
      getRouteMemberships(watchlist).filter((record) =>
        pathSpecsEqual(record.path_spec, path?.path_spec),
      ),
    [getRouteMemberships, path?.path_spec],
  );

  // Check if path is in a specific watchlist
  const isMember = useCallback(
    (watchlist: any): boolean => {
      return getExactMemberships(watchlist).length > 0;
    },
    [getExactMemberships],
  );

  const hasRouteMembership = useCallback(
    (watchlist: any): boolean => getRouteMemberships(watchlist).length > 0,
    [getRouteMemberships],
  );

  // Get path name from a specific watchlist
  const getPathNameFromWatchlist = useCallback(
    (watchlist: any): string | null => {
      const record =
        getExactMemberships(watchlist)[0] ?? getRouteMemberships(watchlist)[0];
      return record?.name || null;
    },
    [getExactMemberships, getRouteMemberships],
  );

  // Check if path is in ANY watchlist
  const isInAnyWatchlist = useMemo(() => {
    if (!pathFingerprint || !watchlists.length) return false;
    return watchlists.some(isMember);
  }, [watchlists, pathFingerprint, isMember]);

  // Get display name (combines all watchlist names)
  const displayName = useMemo(() => {
    if (!pathFingerprint) return undefined;

    const namesInWatchlists: [string, string][] = [];
    watchlists.forEach((w: any) => {
      if (isMember(w)) {
        const name = getPathNameFromWatchlist(w);
        namesInWatchlists.push([w.name, name || '']);
      }
    });

    if (namesInWatchlists.length === 0) return undefined;
    if (namesInWatchlists.length === 1) {
      return namesInWatchlists[0][1] || undefined;
    }

    return namesInWatchlists
      .map(([watchlistName, pathName]) =>
        pathName ? `${watchlistName} : ${pathName}` : null,
      )
      .filter(Boolean)
      .join(' | ');
  }, [pathFingerprint, watchlists, isMember, getPathNameFromWatchlist]);

  return {
    watchlists,
    isMember,
    getPathNameFromWatchlist,
    getRouteMemberships,
    getExactMemberships,
    hasRouteMembership,
    isInAnyWatchlist,
    displayName,
    ...queryState,
  };
}

/**
 * Hook for enriching multiple paths with watchlist display names.
 * Used by PathTable to compute watchlist_path_name for all paths at once.
 */
export function useEnrichedPaths(
  paths: PathReport[] | undefined,
  watchlists: any[],
) {
  return useMemo(() => {
    if (!paths || !watchlists.length) return paths;

    return paths.map((path) => {
      const pathFingerprint = path.original_path_fingerprint;
      if (!pathFingerprint) return path;

      const exactSpecKey = pathSpecKey(path.path_spec);
      let hasRouteMembership = false;
      let hasExactMembership = false;
      const namesInWatchlists: [string, string][] = [];
      watchlists.forEach((w: any) => {
        if (!w.path_records_detail) return;
        const records = w.path_records_detail.filter(
          (r: any) => r?.path_fingerprint === pathFingerprint,
        );
        hasRouteMembership ||= records.length > 0;
        const exactRecord = records.find(
          (record: any) =>
            exactSpecKey !== null && pathSpecKey(record.path_spec) === exactSpecKey,
        );
        if (exactRecord) {
          hasExactMembership = true;
          namesInWatchlists.push([w.name, exactRecord.name || '']);
        }
      });

      if (!hasRouteMembership) {
        return path; // No changes if not in any watchlist
      }

      // Compute display name using same logic as individual hook
      let watchlistPathName: string | undefined;
      if (namesInWatchlists.length === 1) {
        const [_watchlistName, pathName] = namesInWatchlists[0];
        watchlistPathName = pathName || undefined;
      } else {
        watchlistPathName = namesInWatchlists
          .map(([watchlistName, pathName]) =>
            pathName ? `${watchlistName} : ${pathName}` : null,
          )
          .filter(Boolean)
          .join(' | ');
      }

      return {
        ...path,
        watchlist_path_name: watchlistPathName,
        is_in_watchlist: hasExactMembership,
        has_similar_watchlist_path:
          hasRouteMembership && !hasExactMembership,
      };
    });
  }, [paths, watchlists]);
}
